import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ERoutes } from '../enums';
import { useRootStore } from '../stores';
import WebApp from '@twa-dev/sdk';
import { LOCAL_STORAGE_TELEGRAM_INIT_DATA_KEY, request } from '../api';
import {
  getUserProfile,
  getUserProfileInitialFlowData,
  signUp,
} from '../queries';
import { delay } from '../helpers';
import { preloadAppMedia } from '../utils';

export const useGateKeeperRouteChanges = () => {
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const { user } = useRootStore();

  const signUpUserProfile = useCallback(async () => {
    try {
      await signUp();
      await getUserProfileInitialFlowData();
    } catch (error: any) {
      console.error('Error while [signUpUserProfile]', error);
    }
  }, []);

  const loadUserProfile = useCallback(async () => {
    try {
      await preloadAppMedia();
      await getUserProfile();
      await getUserProfileInitialFlowData();
    } catch (error: any) {
      console.error('Error while [loadUserProfile]', error);
    }
  }, []);

  const loadUserFromTelegramSdk = useCallback(async () => {
    try {
      setLoading(true);

      if (!WebApp.initData) {
        await delay(1000);
        loadUserFromTelegramSdk();
        return;
      }

      request.interceptors.request.use((config) => {
        config.headers['Authorization'] = `Bearer ${WebApp.initData}`;
        return config;
      });

      await loadUserProfile();
    } catch (error) {
      console.error('Error while [loadUserFromTelegramSdk]', error);
    } finally {
      setLoading(false);
    }
  }, [loadUserProfile]);

  const loadUserFromLocalStorage = useCallback(async () => {
    try {
      setLoading(true);
      const initDataFromLocalStorage = localStorage.getItem(
        LOCAL_STORAGE_TELEGRAM_INIT_DATA_KEY,
      );

      if (initDataFromLocalStorage) {
        request.interceptors.request.use((config) => {
          config.headers['Authorization'] =
            `Bearer ${initDataFromLocalStorage}`;
          return config;
        });

        await loadUserProfile();
        return;
      }
    } catch (error) {
      console.error('Error while [loadUserFromLocalStorage]', error);
    } finally {
      setLoading(false);
    }
  }, [loadUserProfile]);

  useEffect(() => {
    if (WebApp.platform === 'unknown') {
      loadUserFromLocalStorage();
      return;
    }

    loadUserFromTelegramSdk();
  }, [loadUserFromLocalStorage, loadUserFromTelegramSdk]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (user?.id) {
      navigate(ERoutes.main);
      return;
    }

    if (!user?.id && WebApp.platform === 'unknown') {
      navigate(ERoutes.connectByTelegramOAuth);
      return;
    }

    if (WebApp.initData && !user?.id) {
      signUpUserProfile();
      return;
    }

    if (!WebApp.initData) {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user?.id]);
};
